import { NavLink } from "react-router-dom";
import { Activity, BarChart3, Brain, CalendarDays, CheckSquare, ChevronsLeft, ChevronsRight, FileText, FolderOpen, Megaphone, Newspaper, Radio, Settings, Terminal } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { useWorkspace, useWsQuery } from "@/context/WorkspaceContext";

export const NAV = [
  ["/command-centre", "Command Centre", Terminal], ["/campaigns", "Campaigns", Megaphone], ["/content", "Content", FileText],
  ["/calendar", "Calendar", CalendarDays], ["/approvals", "Approvals", CheckSquare], ["/channels", "Channels", Radio],
  ["/analytics", "Analytics", BarChart3], ["/briefings", "Growth Briefings", Newspaper], ["/brand-brain", "Brand Brain", Brain], ["/assets", "Asset Library", FolderOpen],
  ["/activity", "Activity", Activity], ["/settings", "Settings", Settings],
];

const slug = (p) => p.slice(1);

export default function Sidebar({ collapsed, onToggle, onNavigate }) {
  const { can } = useWorkspace();
  const brand = useWsQuery("/brand");
  const approvals = useWsQuery("/approvals?status=pending");
  const pct = brand.data?.completion?.overall ?? 0;
  const pending = approvals.data?.length || 0;

  return (
    <aside data-testid="sidebar" className={`h-full flex flex-col bg-[#050816] border-r border-[#243153] transition-[width] duration-200 ${collapsed ? "w-[72px]" : "w-[256px]"}`}>
      <div className={`h-16 flex items-center ${collapsed ? "justify-center" : "px-5"} border-b border-[#172342]`}>
        <Logo size={collapsed ? 34 : 36} showWord={!collapsed} />
      </div>
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
        {NAV.filter(([to]) => to !== "/activity" || can("view_audit")).map(([to, label, Icon]) => (
          <NavLink key={to} to={to} onClick={onNavigate} data-testid={`sidebar-nav-${slug(to)}`} title={collapsed ? label : undefined}
            className={({ isActive }) => `group flex items-center gap-3 rounded-lg ${collapsed ? "justify-center px-0" : "px-3"} py-2.5 text-[13.5px] font-medium transition-colors ${isActive ? "cq-nav-active" : "text-[#AAB6CC] hover:text-[#F7FAFF] hover:bg-[#0E1733]"}`}>
            {({ isActive }) => (<>
              <Icon size={17} className={isActive ? "text-[#22E6F2]" : "text-[#7C8BAD] group-hover:text-[#AAB6CC]"} />
              {!collapsed && <span className="flex-1">{label}</span>}
              {!collapsed && to === "/approvals" && pending > 0 && (
                <span data-testid="sidebar-approvals-count" className="font-mono text-[10.5px] px-1.5 py-0.5 rounded bg-[#F4B860]/15 text-[#F4B860]">{pending}</span>
              )}
            </>)}
          </NavLink>
        ))}
      </nav>
      {!collapsed && (
        <NavLink to="/brand-brain" onClick={onNavigate} data-testid="brand-brain-completion-widget" className="mx-3 mb-3 p-4 rounded-xl border border-[#243153] bg-[#0A1024] hover:border-[#22E6F2]/40 transition-colors">
          <div className="flex items-center justify-between">
            <span className="cq-muted-label">Brand Brain</span>
            <span className="font-mono text-xs text-[#22E6F2]">{pct}%</span>
          </div>
          <div className="mt-2.5 h-1.5 rounded-full bg-[#172342] overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "var(--cq-gradient)" }} />
          </div>
          <p className="mt-2 text-[11.5px] text-[#7C8BAD]">Richer context, sharper strategies.</p>
        </NavLink>
      )}
      {onToggle && (
        <button data-testid="sidebar-collapse-toggle" onClick={onToggle} className="h-11 border-t border-[#172342] flex items-center justify-center gap-2 text-xs text-[#7C8BAD] hover:text-[#F7FAFF]">
          {collapsed ? <ChevronsRight size={16} /> : <><ChevronsLeft size={16} /> Collapse</>}
        </button>
      )}
    </aside>
  );
}
